import React from 'react'
import { Box } from '@mui/material'

import { BEIGE_400, BLUE_600, BOX_SHADOW_LG, GRAY_800, WHITE } from 'constants/styling/theme'

/** Diameter of a scroll arrow. */
export const CAROUSEL_ARROW_SIZE = '4rem'

interface Props {
  direction: 'prev' | 'next'
  label: string
  onClick: (direction: 'prev' | 'next') => void
  /** Vertical centre of the arrow within its row. */
  top: string
  /** Distance from the row's edge; negative hangs the arrow outside it. */
  inset: string
  'data-cy'?: string
}

/**
 * A carousel's scroll arrow. Rendered only when there is something that way, which is why it carries no
 * disabled state: an arrow pointing nowhere is noise.
 */
export const CarouselScrollArrow: React.FC<Props> = ({ direction, label, onClick, top, inset, 'data-cy': dataCy }) => (
  <Box
    component="button"
    type="button"
    aria-label={label}
    data-cy={dataCy}
    onClick={() => onClick(direction)}
    sx={{
      position: 'absolute',
      top,
      transform: 'translateY(-50%)',
      zIndex: 2,
      [direction === 'prev' ? 'left' : 'right']: inset,
      width: CAROUSEL_ARROW_SIZE,
      height: CAROUSEL_ARROW_SIZE,
      p: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '50%',
      backgroundColor: WHITE,
      boxShadow: BOX_SHADOW_LG,
      color: GRAY_800,
      cursor: 'pointer',
      transition: 'background-color 120ms ease',
      '&:hover': { backgroundColor: BEIGE_400 },
      '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: '2px' },
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      // Sized through sx: a `width` prop on Box is a system prop, not the SVG
      // attribute, and "20" is dropped — the glyph then filled the whole button.
      sx={{ display: 'block', width: '2rem', height: '2rem', transform: direction === 'next' ? 'rotate(180deg)' : undefined }}
    >
      <line x1="19" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" />
      <polyline points="11 6 5 12 11 18" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  </Box>
)
